import React, { memo } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import LynkLogoImg from 'src/features/lynk/assets/current-lynk-logo.png'
import { useGetPost } from 'src/shared/api/feeds/use-get-post'
import { useMatchById } from 'src/shared/api/partner-match/use-match-by-id'
import { ProfileAvatar } from 'src/shared/features/users/components/profile-photos/profile-avatar'
import type { PublicUserData } from 'src/shared/features/users/schemas/user'
import { NotificationVerb } from 'src/shared/schemas/notifications'
import type { NotificationActivity, NotificationGroup } from 'src/shared/schemas/stream'
import { cn } from 'src/shared/utils/cn'

import MatchIconImg from '../assets/certified-match.png'
import WeeklyRecapIconImg from '../assets/inverse-view-visibility-score.png'
import SetupPhoneGreyscaleIconImg from '../assets/setup-phone-greyscale.png'
import SetupPhoneIconImg from '../assets/setup-phone.png'
import SystemIconImg from '../assets/system.png'
import EnterpriseInvitationIcon from '../components/enterprise/ep-icon'
import EnterpriseInvitationSubtitle from '../components/enterprise/ep-subtitle'
import EnterpriseInvitationTitle from '../components/enterprise/ep-title'
import FeedRightContent from '../components/feed/feed-right-content'
import LeadInquiryIcon from '../components/lead-inquiry/li-icon'
import LeadInquirySubtitle from '../components/lead-inquiry/li-subtitle'
import LynkWorkflowSubtitle from '../components/lynk-workflow/lw-subtitle'
import LynkWorkflowTitle from '../components/lynk-workflow/lw-title'
import PointIcon from '../components/points/point-icon'
import PointRightContent from '../components/points/point-right-content'
import PointSubtitle from '../components/points/point-subtitle'
import PointTitle from '../components/points/point-title'
import TeamInvitationIcon from '../components/team-invitation/ti-icon'
import TeamInvitationSubtitle from '../components/team-invitation/ti-subtitle'
import TeamInvitationTitle from '../components/team-invitation/ti-title'
import { useNotificationModals } from '../context/notification-modals-context'

const ProfileLeftIcon = ({ id }: { id: number }) => (
  <div className="flex shrink-0 cursor-pointer flex-col items-center gap-3 rounded-full border-2 border-white bg-white shadow-[0_2px_12px_rgba(27,48,41,0.1)]">
    <ProfileAvatar size={45} userId={id} className="box-content" />
  </div>
)

const isValidNotificationVerb = (verb: any): verb is NotificationVerb => {
  return Object.values(NotificationVerb).includes(verb)
}

const getFirstActorId = (activity?: NotificationActivity): number => {
  try {
    if (!activity?.actor) {
      return -1
    }

    const [, actorId] = activity.actor.split(':')
    const parsedId = parseInt(actorId)

    return isNaN(parsedId) ? -1 : parsedId
  } catch (error) {
    console.error('Error extracting actor ID:', error)
    return -1
  }
}

const MatchIcon = () => (
  <img
    src={MatchIconImg}
    alt="match icon"
    className="size-[49px] rounded-[12px] object-contain shadow-[0_2px_12px_rgba(27,48,41,0.1)]"
  />
)

// Memoized SystemIcon component to prevent unnecessary re-renders
const SystemIcon = ({
  source,
  className,
  Icon,
  isLynk = false,
}: {
  source?: string
  className?: string
  Icon?: React.ReactNode
  isLynk?: boolean
}) => {
  return (
    <div
      className={cn(
        'flex h-[49px] w-[49px] items-center justify-center rounded-[10px] border-2 border-white shadow-[0_2px_12px_rgba(27,48,41,0.1)]',
        className,
      )}
    >
      {isLynk ? (
        // <AnimatedLynkLogo autoPlay={false} width={30} height={30} />
        <img src={LynkLogoImg} alt="current lynk logo" className="size-[30px] object-contain" />
      ) : source ? (
        <img src={source} alt="system icon" className="size-[30px] object-contain" />
      ) : (
        Icon
      )}
    </div>
  )
}

const MemoizedSystemIcon = memo(SystemIcon)

const isMatch = (activity?: NotificationActivity) => activity?.verb === NotificationVerb.PARTNER_MATCH_NEW_MESSAGE

const getLinkId = (activity: NotificationActivity) => {
  try {
    const objectType = activity.object?.split(':')[0] || ''

    // Handle each object type with proper null checks
    switch (objectType) {
      case 'partner_match':
      case 'message':
        return activity.data?.chatId?.toString() || null
      case 'new_message':
        return activity.data?.chatId?.toString() || null

      case 'like':
      case 'comment': {
        // Wrap lexical declarations in a block to avoid "unexpected lexical declaration in case block"
        const id = activity.data?.parentId?.toString() || activity.object?.split(':')[1] || null
        return id
      }

      case 'referral':
      case 'referral_share': {
        const id = activity.foreignId?.split(':')[1] || null
        return id
      }

      case 'team_invitation': {
        const id = activity.foreignId?.split(':')[1] || null
        return id
      }

      default: {
        // For unknown types, try to get ID from object
        const defaultId = activity.object?.split(':')[1]
        if (!defaultId) {
          console.warn('Unable to extract ID from activity:', activity)
          return null
        }
        return defaultId
      }
    }
  } catch (error) {
    console.error('Error extracting link ID:', error, activity)
    return null
  }
}

const NotificationTitleMap: {
  [key in NotificationVerb]: ((activity?: NotificationActivity) => React.ReactNode | null) | null
} = {
  [NotificationVerb.NEW_REFERRAL_SHARE]: () => null,
  [NotificationVerb.REFERRAL_SHARE_RECEIVER_TIME_BASED]: () => null,
  [NotificationVerb.REFERRAL_SHARE_SENDER_TIME_BASED]: () => null,
  [NotificationVerb.REFERRAL_SHARE_INTERESTED]: () => null,
  [NotificationVerb.REFERRAL_SHARE_APPROVED]: () => null,
  [NotificationVerb.REFERRAL_SHARE_RECEIVER_DECLINED]: () => null,
  [NotificationVerb.REFERRAL_SHARE_SENDER_DENIED]: () => null,
  [NotificationVerb.REFERRAL_SHARE_EXPIRED]: () => null,
  [NotificationVerb.TEAM_INVITATION_APPROVED]: (activity) => {
    const invitationData = activity?.data as any
    return (
      <TeamInvitationTitle
        consumerName={invitationData.consumerName}
        referredByName={invitationData.referredByName}
        referredToName={invitationData.referredToName}
        notificationVerb={activity?.verb || null}
      />
    )
  },
  [NotificationVerb.TEAM_INVITATION_DECLINED_BY_INVITEE]: (activity) => {
    const invitationData = activity?.data as any
    return (
      <TeamInvitationTitle
        consumerName={invitationData.consumerName}
        referredByName={invitationData.referredByName}
        referredToName={invitationData.referredToName}
        notificationVerb={activity?.verb || null}
      />
    )
  },
  [NotificationVerb.TEAM_INVITATION_DECLINED_BY_CONSUMER]: (activity) => {
    const invitationData = activity?.data as any
    return (
      <TeamInvitationTitle
        consumerName={invitationData.consumerName}
        referredByName={invitationData.referredByName}
        referredToName={invitationData.referredToName}
        notificationVerb={activity?.verb || null}
      />
    )
  },
  [NotificationVerb.TEAM_INVITATION_EXPIRED]: (activity) => {
    const invitationData = activity?.data as any
    return (
      <TeamInvitationTitle
        consumerName={invitationData.consumerName}
        referredByName={invitationData.referredByName}
        referredToName={invitationData.referredToName}
        notificationVerb={activity?.verb || null}
      />
    )
  },
  [NotificationVerb.TEAM_INVITATION_SENT]: (activity) => {
    const invitationData = activity?.data as any
    return (
      <TeamInvitationTitle
        consumerName={invitationData.consumerName}
        referredByName={invitationData.referredByName}
        referredToName={invitationData.referredToName}
        notificationVerb={activity?.verb || null}
      />
    )
  },
  [NotificationVerb.TEAM_INVITATION_INTERESTED]: (activity) => {
    const invitationData = activity?.data as any
    return (
      <TeamInvitationTitle
        consumerName={invitationData.consumerName}
        referredByName={invitationData.referredByName}
        referredToName={invitationData.referredToName}
        notificationVerb={activity?.verb || null}
      />
    )
  },
  [NotificationVerb.TEAM_INVITATION_AUTO_REJECTED]: (activity) => {
    const invitationData = activity?.data as any
    return (
      <TeamInvitationTitle
        consumerName={invitationData.consumerName}
        referredByName={invitationData.referredByName}
        referredToName={invitationData.referredToName}
        notificationVerb={activity?.verb || null}
      />
    )
  },
  [NotificationVerb.LICENSE_VERIFICATION]: null,
  [NotificationVerb.POINTS_RECEIVED]: () => <PointTitle />,
  [NotificationVerb.SYSTEM]: null,
  [NotificationVerb.NEW_COMMENT_ON_POST]: () => null,
  [NotificationVerb.NEW_COMMENT_ON_COMMENT]: () => null,
  [NotificationVerb.NEW_LIKE_ON_COMMENT]: () => null,
  [NotificationVerb.NEW_LIKE_ON_POST]: () => null,
  [NotificationVerb.NEW_MESSAGE]: () => null,
  [NotificationVerb.REFERRAL_NEW_MESSAGE]: () => null,
  [NotificationVerb.PARTNER_MATCH_NEW_MESSAGE]: () => null,
  [NotificationVerb.NETWORK_NEW_MESSAGE]: () => null,
  [NotificationVerb.SNEAK_NEW_MESSAGE]: () => null,
  [NotificationVerb.PARTNER_MATCH_WEEKLY_RESET]: null,
  [NotificationVerb.WEEKLY_RECAP]: null,
  [NotificationVerb.PARTNER_MATCH_UNLOCKED]: null,
  [NotificationVerb.LEAD_INQUIRY_RECEIVED]: null,
  [NotificationVerb.LEAD_INQUIRY_REMINDER]: null,
  [NotificationVerb.LEAD_INQUIRY_COMPETITION]: null,
  [NotificationVerb.LEAD_INQUIRY_TAKEN]: null,
  [NotificationVerb.LEAD_CREDIT_REWARD_RECEIVED]: null,
  [NotificationVerb.ONE_TIME]: null,
  [NotificationVerb.TWILIO_NUMBER_SELECTION]: () => (
    <LynkWorkflowTitle notificationVerb={NotificationVerb.TWILIO_NUMBER_SELECTION} />
  ),
  [NotificationVerb.TWILIO_BRAND_FAILED]: () => (
    <LynkWorkflowTitle notificationVerb={NotificationVerb.TWILIO_BRAND_FAILED} />
  ),
  [NotificationVerb.TWILIO_CAMPAIGN_FAILED]: () => (
    <LynkWorkflowTitle notificationVerb={NotificationVerb.TWILIO_CAMPAIGN_FAILED} />
  ),
  [NotificationVerb.LYNK_DISABLED]: (activity) => {
    const lynkData = activity?.data as any
    return (
      <LynkWorkflowTitle
        notificationVerb={NotificationVerb.LYNK_DISABLED}
        consumerFirstName={lynkData.consumerFirstName}
      />
    )
  },
  [NotificationVerb.LYNK_FAILED]: (activity) => {
    const lynkData = activity?.data as any
    return (
      <LynkWorkflowTitle
        notificationVerb={NotificationVerb.LYNK_FAILED}
        consumerFirstName={lynkData.consumerFirstName}
      />
    )
  },
  [NotificationVerb.LYNK_COMPLETED]: (activity) => {
    const lynkData = activity?.data as any
    return (
      <LynkWorkflowTitle
        notificationVerb={NotificationVerb.LYNK_COMPLETED}
        consumerFirstName={lynkData.consumerFirstName}
      />
    )
  },
  [NotificationVerb.LYNK_NEED_ASSISTANCE]: (activity) => {
    const lynkData = activity?.data as any
    return (
      <LynkWorkflowTitle
        notificationVerb={NotificationVerb.LYNK_NEED_ASSISTANCE}
        consumerFirstName={lynkData.consumerFirstName}
      />
    )
  },
  [NotificationVerb.LYNK_HOSTILE]: (activity) => {
    const lynkData = activity?.data as any
    return (
      <LynkWorkflowTitle
        notificationVerb={NotificationVerb.LYNK_HOSTILE}
        consumerFirstName={lynkData.consumerFirstName}
      />
    )
  },
  [NotificationVerb.LYNK_EXTREME_CASE]: (activity) => {
    const lynkData = activity?.data as any
    return (
      <LynkWorkflowTitle
        notificationVerb={NotificationVerb.LYNK_EXTREME_CASE}
        consumerFirstName={lynkData.consumerFirstName}
      />
    )
  },
  [NotificationVerb.LYNK_OFF_TOPIC]: (activity) => {
    const lynkData = activity?.data as any
    return (
      <LynkWorkflowTitle
        notificationVerb={NotificationVerb.LYNK_OFF_TOPIC}
        consumerFirstName={lynkData.consumerFirstName}
      />
    )
  },
  [NotificationVerb.ENTERPRISE_INVITATION_SENT]: () => {
    return <EnterpriseInvitationTitle notificationVerb={NotificationVerb.ENTERPRISE_INVITATION_SENT} />
  },
  [NotificationVerb.ENTERPRISE_INVITATION_ACCEPTED]: () => {
    return <EnterpriseInvitationTitle notificationVerb={NotificationVerb.ENTERPRISE_INVITATION_ACCEPTED} />
  },
  [NotificationVerb.ENTERPRISE_INVITATION_DECLINED]: () => {
    return <EnterpriseInvitationTitle notificationVerb={NotificationVerb.ENTERPRISE_INVITATION_DECLINED} />
  },
  [NotificationVerb.ENTERPRISE_INVITATION_CANCELED]: () => {
    return <EnterpriseInvitationTitle notificationVerb={NotificationVerb.ENTERPRISE_INVITATION_CANCELED} />
  },
  [NotificationVerb.ENTERPRISE_INVITATION_EXPIRED]: () => {
    return <EnterpriseInvitationTitle notificationVerb={NotificationVerb.ENTERPRISE_INVITATION_EXPIRED} />
  },
} as const

const NotificationSubtitleMap: {
  [key in NotificationVerb]: ((activity?: NotificationActivity) => React.ReactNode | null) | null
} = {
  [NotificationVerb.NEW_REFERRAL_SHARE]: () => null,
  [NotificationVerb.REFERRAL_SHARE_RECEIVER_TIME_BASED]: () => null,
  [NotificationVerb.REFERRAL_SHARE_SENDER_TIME_BASED]: () => null,
  [NotificationVerb.REFERRAL_SHARE_INTERESTED]: () => null,
  [NotificationVerb.REFERRAL_SHARE_APPROVED]: () => null,
  [NotificationVerb.REFERRAL_SHARE_RECEIVER_DECLINED]: () => null,
  [NotificationVerb.REFERRAL_SHARE_SENDER_DENIED]: () => null,
  [NotificationVerb.REFERRAL_SHARE_EXPIRED]: () => null,
  [NotificationVerb.TEAM_INVITATION_APPROVED]: (activity) => {
    const invitationData = activity?.data as any
    return <TeamInvitationSubtitle consumerName={invitationData.consumerName} clientType={invitationData.clientType} />
  },
  [NotificationVerb.TEAM_INVITATION_DECLINED_BY_INVITEE]: (activity) => {
    const invitationData = activity?.data as any
    return <TeamInvitationSubtitle consumerName={invitationData.consumerName} clientType={invitationData.clientType} />
  },
  [NotificationVerb.TEAM_INVITATION_DECLINED_BY_CONSUMER]: (activity) => {
    const invitationData = activity?.data as any
    return <TeamInvitationSubtitle consumerName={invitationData.consumerName} clientType={invitationData.clientType} />
  },
  [NotificationVerb.TEAM_INVITATION_EXPIRED]: (activity) => {
    const invitationData = activity?.data as any
    return <TeamInvitationSubtitle consumerName={invitationData.consumerName} clientType={invitationData.clientType} />
  },
  [NotificationVerb.TEAM_INVITATION_SENT]: (activity) => {
    const invitationData = activity?.data as any
    return <TeamInvitationSubtitle consumerName={invitationData.consumerName} clientType={invitationData.clientType} />
  },
  [NotificationVerb.TEAM_INVITATION_INTERESTED]: (activity) => {
    const invitationData = activity?.data as any
    return <TeamInvitationSubtitle consumerName={invitationData.consumerName} clientType={invitationData.clientType} />
  },
  [NotificationVerb.TEAM_INVITATION_AUTO_REJECTED]: (activity) => {
    const invitationData = activity?.data as any
    return <TeamInvitationSubtitle consumerName={invitationData.consumerName} clientType={invitationData.clientType} />
  },
  [NotificationVerb.LICENSE_VERIFICATION]: null,
  [NotificationVerb.POINTS_RECEIVED]: (activity) => {
    const pointData = activity?.data as any
    return (
      <PointSubtitle
        inviterName={pointData.inviterName}
        inviteeName={pointData.inviteeName}
        text={pointData.text}
        points={pointData.pointsAmount}
        reason={pointData.pointsRecordReason}
        message={pointData.customMessage}
      />
    )
  },
  [NotificationVerb.SYSTEM]: null,
  [NotificationVerb.NEW_COMMENT_ON_POST]: () => null,
  [NotificationVerb.NEW_COMMENT_ON_COMMENT]: () => null,
  [NotificationVerb.NEW_LIKE_ON_COMMENT]: () => null,
  [NotificationVerb.NEW_LIKE_ON_POST]: () => null,
  [NotificationVerb.NEW_MESSAGE]: () => null,
  [NotificationVerb.REFERRAL_NEW_MESSAGE]: () => null,
  [NotificationVerb.PARTNER_MATCH_NEW_MESSAGE]: () => null,
  [NotificationVerb.NETWORK_NEW_MESSAGE]: () => null,
  [NotificationVerb.SNEAK_NEW_MESSAGE]: () => null,
  [NotificationVerb.PARTNER_MATCH_WEEKLY_RESET]: null,
  [NotificationVerb.WEEKLY_RECAP]: null,
  [NotificationVerb.PARTNER_MATCH_UNLOCKED]: null,
  [NotificationVerb.LEAD_INQUIRY_RECEIVED]: (activity) => {
    const leadInquiryData = activity?.data as any
    return <LeadInquirySubtitle consumerName={leadInquiryData.consumerName} clientType={leadInquiryData.clientType} />
  },
  [NotificationVerb.LEAD_INQUIRY_REMINDER]: (activity) => {
    const leadInquiryData = activity?.data as any
    return <LeadInquirySubtitle consumerName={leadInquiryData.consumerName} clientType={leadInquiryData.clientType} />
  },
  [NotificationVerb.LEAD_INQUIRY_COMPETITION]: (activity) => {
    const leadInquiryData = activity?.data as any
    return <LeadInquirySubtitle consumerName={leadInquiryData.consumerName} clientType={leadInquiryData.clientType} />
  },
  [NotificationVerb.LEAD_INQUIRY_TAKEN]: (activity) => {
    const leadInquiryData = activity?.data as any
    return <LeadInquirySubtitle consumerName={leadInquiryData.consumerName} clientType={leadInquiryData.clientType} />
  },
  [NotificationVerb.LEAD_CREDIT_REWARD_RECEIVED]: (activity) => {
    const leadInquiryData = activity?.data as any
    return <LeadInquirySubtitle consumerName={leadInquiryData.consumerName} clientType={leadInquiryData.clientType} />
  },
  [NotificationVerb.ONE_TIME]: null,
  [NotificationVerb.TWILIO_NUMBER_SELECTION]: () => (
    <LynkWorkflowSubtitle notificationVerb={NotificationVerb.TWILIO_NUMBER_SELECTION} />
  ),
  [NotificationVerb.TWILIO_BRAND_FAILED]: () => (
    <LynkWorkflowSubtitle notificationVerb={NotificationVerb.TWILIO_BRAND_FAILED} />
  ),
  [NotificationVerb.TWILIO_CAMPAIGN_FAILED]: () => (
    <LynkWorkflowSubtitle notificationVerb={NotificationVerb.TWILIO_CAMPAIGN_FAILED} />
  ),
  [NotificationVerb.LYNK_DISABLED]: () => {
    return <LynkWorkflowSubtitle notificationVerb={NotificationVerb.LYNK_DISABLED} />
  },
  [NotificationVerb.LYNK_FAILED]: () => {
    return <LynkWorkflowSubtitle notificationVerb={NotificationVerb.LYNK_FAILED} />
  },
  [NotificationVerb.LYNK_COMPLETED]: () => {
    return <LynkWorkflowSubtitle notificationVerb={NotificationVerb.LYNK_COMPLETED} />
  },
  [NotificationVerb.LYNK_NEED_ASSISTANCE]: () => {
    return <LynkWorkflowSubtitle notificationVerb={NotificationVerb.LYNK_NEED_ASSISTANCE} />
  },
  [NotificationVerb.LYNK_HOSTILE]: () => {
    return <LynkWorkflowSubtitle notificationVerb={NotificationVerb.LYNK_HOSTILE} />
  },
  [NotificationVerb.LYNK_EXTREME_CASE]: () => {
    return <LynkWorkflowSubtitle notificationVerb={NotificationVerb.LYNK_EXTREME_CASE} />
  },
  [NotificationVerb.LYNK_OFF_TOPIC]: () => {
    return <LynkWorkflowSubtitle notificationVerb={NotificationVerb.LYNK_OFF_TOPIC} />
  },
  [NotificationVerb.ENTERPRISE_INVITATION_SENT]: (activity) => {
    const enterpriseData = activity?.data as any
    return (
      <EnterpriseInvitationSubtitle
        notificationVerb={NotificationVerb.ENTERPRISE_INVITATION_SENT}
        enterpriseName={enterpriseData.enterpriseName || ''}
      />
    )
  },
  [NotificationVerb.ENTERPRISE_INVITATION_ACCEPTED]: (activity) => {
    const enterpriseData = activity?.data as any
    return (
      <EnterpriseInvitationSubtitle
        notificationVerb={NotificationVerb.ENTERPRISE_INVITATION_ACCEPTED}
        enterpriseName={enterpriseData.enterpriseName || ''}
      />
    )
  },
  [NotificationVerb.ENTERPRISE_INVITATION_DECLINED]: (activity) => {
    const enterpriseData = activity?.data as any
    return (
      <EnterpriseInvitationSubtitle
        notificationVerb={NotificationVerb.ENTERPRISE_INVITATION_DECLINED}
        enterpriseName={enterpriseData.enterpriseName || ''}
      />
    )
  },
  [NotificationVerb.ENTERPRISE_INVITATION_CANCELED]: (activity) => {
    const enterpriseData = activity?.data as any
    return (
      <EnterpriseInvitationSubtitle
        notificationVerb={NotificationVerb.ENTERPRISE_INVITATION_CANCELED}
        enterpriseName={enterpriseData.enterpriseName || ''}
      />
    )
  },
  [NotificationVerb.ENTERPRISE_INVITATION_EXPIRED]: (activity) => {
    const enterpriseData = activity?.data as any
    return (
      <EnterpriseInvitationSubtitle
        notificationVerb={NotificationVerb.ENTERPRISE_INVITATION_EXPIRED}
        enterpriseName={enterpriseData.enterpriseName || ''}
      />
    )
  },
} as const

const NotificationIconMap: { [key in NotificationVerb]: (activity?: NotificationActivity) => React.ReactNode | null } =
  {
    // feed
    [NotificationVerb.NEW_COMMENT_ON_POST]: (activity) => <ProfileLeftIcon id={getFirstActorId(activity)} />,
    [NotificationVerb.NEW_COMMENT_ON_COMMENT]: (activity) => <ProfileLeftIcon id={getFirstActorId(activity)} />,
    [NotificationVerb.NEW_LIKE_ON_COMMENT]: (activity) => <ProfileLeftIcon id={getFirstActorId(activity)} />,
    [NotificationVerb.NEW_LIKE_ON_POST]: (activity) => <ProfileLeftIcon id={getFirstActorId(activity)} />,

    [NotificationVerb.NEW_REFERRAL_SHARE]: (activity) => <ProfileLeftIcon id={getFirstActorId(activity)} />,
    [NotificationVerb.REFERRAL_SHARE_RECEIVER_TIME_BASED]: (activity) => (
      <ProfileLeftIcon id={getFirstActorId(activity)} />
    ),
    [NotificationVerb.REFERRAL_SHARE_SENDER_TIME_BASED]: (activity) => (
      <ProfileLeftIcon id={getFirstActorId(activity)} />
    ),
    [NotificationVerb.REFERRAL_SHARE_INTERESTED]: (activity) => <ProfileLeftIcon id={getFirstActorId(activity)} />,
    [NotificationVerb.REFERRAL_SHARE_APPROVED]: (activity) => <ProfileLeftIcon id={getFirstActorId(activity)} />,
    [NotificationVerb.REFERRAL_SHARE_RECEIVER_DECLINED]: (activity) => (
      <ProfileLeftIcon id={getFirstActorId(activity)} />
    ),
    [NotificationVerb.REFERRAL_SHARE_SENDER_DENIED]: (activity) => <ProfileLeftIcon id={getFirstActorId(activity)} />,
    [NotificationVerb.REFERRAL_SHARE_EXPIRED]: (activity) => <ProfileLeftIcon id={getFirstActorId(activity)} />,
    [NotificationVerb.LICENSE_VERIFICATION]: () => <MemoizedSystemIcon source={SystemIconImg} className="bg-primary" />,
    [NotificationVerb.POINTS_RECEIVED]: (activity) => {
      const pointData = activity?.data as any
      return <PointIcon reason={pointData.pointsRecordReason} />
    },
    [NotificationVerb.WEEKLY_RECAP]: () => <MemoizedSystemIcon source={WeeklyRecapIconImg} className="bg-primary" />,
    [NotificationVerb.SYSTEM]: () => <MemoizedSystemIcon source={SystemIconImg} className="bg-primary" />,
    [NotificationVerb.PARTNER_MATCH_WEEKLY_RESET]: () => <MatchIcon />,
    [NotificationVerb.PARTNER_MATCH_UNLOCKED]: () => <MatchIcon />,

    [NotificationVerb.NEW_MESSAGE]: (activity) => <ProfileLeftIcon id={getFirstActorId(activity)} />,
    [NotificationVerb.REFERRAL_NEW_MESSAGE]: (activity) => <ProfileLeftIcon id={getFirstActorId(activity)} />,
    [NotificationVerb.NETWORK_NEW_MESSAGE]: (activity) => <ProfileLeftIcon id={getFirstActorId(activity)} />,
    [NotificationVerb.SNEAK_NEW_MESSAGE]: (activity) => <ProfileLeftIcon id={getFirstActorId(activity)} />,
    [NotificationVerb.PARTNER_MATCH_NEW_MESSAGE]: (activity) =>
      isMatch(activity) ? <MatchIcon /> : <ProfileLeftIcon id={getFirstActorId(activity)} />,
    [NotificationVerb.LEAD_INQUIRY_RECEIVED]: (activity) => {
      const leadInquiryData = activity?.data as any
      return <LeadInquiryIcon mapSnapshot={leadInquiryData.mapSnapshot} />
    },
    [NotificationVerb.LEAD_INQUIRY_REMINDER]: (activity) => {
      const leadInquiryData = activity?.data as any
      return <LeadInquiryIcon mapSnapshot={leadInquiryData.mapSnapshot} />
    },
    [NotificationVerb.LEAD_INQUIRY_COMPETITION]: (activity) => {
      const leadInquiryData = activity?.data as any
      return <LeadInquiryIcon mapSnapshot={leadInquiryData.mapSnapshot} />
    },
    [NotificationVerb.LEAD_INQUIRY_TAKEN]: (activity) => {
      const leadInquiryData = activity?.data as any
      return <LeadInquiryIcon mapSnapshot={leadInquiryData.mapSnapshot} />
    },
    [NotificationVerb.ONE_TIME]: () => <MemoizedSystemIcon source={WeeklyRecapIconImg} className="bg-primary" />,
    [NotificationVerb.LEAD_CREDIT_REWARD_RECEIVED]: () => <LeadInquiryIcon mapSnapshot={''} />,
    [NotificationVerb.TEAM_INVITATION_APPROVED]: (activity) => {
      const invitationData = activity?.data as any
      return (
        <TeamInvitationIcon
          mapSnapshot={invitationData.mapSnapshot}
          referredByProfilePhoto={invitationData.referredByProfilePhoto}
        />
      )
    },
    [NotificationVerb.TEAM_INVITATION_DECLINED_BY_INVITEE]: (activity) => {
      const invitationData = activity?.data as any
      return (
        <TeamInvitationIcon
          mapSnapshot={invitationData.mapSnapshot}
          referredByProfilePhoto={invitationData.referredByProfilePhoto}
        />
      )
    },

    [NotificationVerb.TEAM_INVITATION_DECLINED_BY_CONSUMER]: (activity) => {
      const invitationData = activity?.data as any
      return (
        <TeamInvitationIcon
          mapSnapshot={invitationData.mapSnapshot}
          referredByProfilePhoto={invitationData.referredByProfilePhoto}
        />
      )
    },
    [NotificationVerb.TEAM_INVITATION_EXPIRED]: (activity) => {
      const invitationData = activity?.data as any
      return (
        <TeamInvitationIcon
          mapSnapshot={invitationData.mapSnapshot}
          referredByProfilePhoto={invitationData.referredByProfilePhoto}
        />
      )
    },
    [NotificationVerb.TEAM_INVITATION_SENT]: (activity) => {
      const invitationData = activity?.data as any
      return (
        <TeamInvitationIcon
          mapSnapshot={invitationData.mapSnapshot}
          referredByProfilePhoto={invitationData.referredByProfilePhoto}
        />
      )
    },
    [NotificationVerb.TEAM_INVITATION_INTERESTED]: (activity) => {
      const invitationData = activity?.data as any
      return (
        <TeamInvitationIcon
          mapSnapshot={invitationData.mapSnapshot}
          referredByProfilePhoto={invitationData.referredByProfilePhoto}
        />
      )
    },
    [NotificationVerb.TEAM_INVITATION_AUTO_REJECTED]: (activity) => {
      const invitationData = activity?.data as any
      return (
        <TeamInvitationIcon
          mapSnapshot={invitationData.mapSnapshot}
          referredByProfilePhoto={invitationData.referredByProfilePhoto}
        />
      )
    },
    [NotificationVerb.TWILIO_NUMBER_SELECTION]: () => (
      <MemoizedSystemIcon className="bg-accent" source={SetupPhoneIconImg} />
    ),
    [NotificationVerb.TWILIO_BRAND_FAILED]: () => (
      <MemoizedSystemIcon className="bg-accent" source={SetupPhoneGreyscaleIconImg} />
    ),
    [NotificationVerb.TWILIO_CAMPAIGN_FAILED]: () => (
      <MemoizedSystemIcon className="bg-accent" source={SetupPhoneGreyscaleIconImg} />
    ),
    [NotificationVerb.LYNK_DISABLED]: () => <MemoizedSystemIcon isLynk={true} className="bg-primary" />,
    [NotificationVerb.LYNK_FAILED]: () => <MemoizedSystemIcon isLynk={true} className="bg-primary" />,
    [NotificationVerb.LYNK_COMPLETED]: () => <MemoizedSystemIcon isLynk={true} className="bg-primary" />,
    [NotificationVerb.LYNK_NEED_ASSISTANCE]: () => <MemoizedSystemIcon isLynk={true} className="bg-primary" />,
    [NotificationVerb.LYNK_HOSTILE]: () => <MemoizedSystemIcon isLynk={true} className="bg-primary" />,
    [NotificationVerb.LYNK_EXTREME_CASE]: () => <MemoizedSystemIcon isLynk={true} className="bg-primary" />,
    [NotificationVerb.LYNK_OFF_TOPIC]: () => <MemoizedSystemIcon isLynk={true} className="bg-primary" />,
    [NotificationVerb.ENTERPRISE_INVITATION_SENT]: (activity) => {
      const enterpriseData = activity?.data as any
      return <EnterpriseInvitationIcon enterpriseLogo={enterpriseData.enterpriseLogo || ''} />
    },
    [NotificationVerb.ENTERPRISE_INVITATION_ACCEPTED]: (activity) => {
      const enterpriseData = activity?.data as any
      return <EnterpriseInvitationIcon enterpriseLogo={enterpriseData.enterpriseLogo || ''} />
    },
    [NotificationVerb.ENTERPRISE_INVITATION_DECLINED]: (activity) => {
      const enterpriseData = activity?.data as any
      return <EnterpriseInvitationIcon enterpriseLogo={enterpriseData.enterpriseLogo || ''} />
    },
    [NotificationVerb.ENTERPRISE_INVITATION_CANCELED]: (activity) => {
      const enterpriseData = activity?.data as any
      return <EnterpriseInvitationIcon enterpriseLogo={enterpriseData.enterpriseLogo || ''} />
    },
    [NotificationVerb.ENTERPRISE_INVITATION_EXPIRED]: (activity) => {
      const enterpriseData = activity?.data as any
      return <EnterpriseInvitationIcon enterpriseLogo={enterpriseData.enterpriseLogo || ''} />
    },
  } as const

interface NotificationContent {
  text: string | React.ReactNode
  onPress: (() => void) | null
  image: string | null
  icon: React.ReactNode | null
  title: React.ReactNode | null
  subtitle: React.ReactNode | null
  rightContent: React.ReactNode | null
}

const useUserNotificationContent = (
  activity: NotificationActivity,
  group: NotificationGroup,
  firstUser: PublicUserData | null,
): NotificationContent => {
  const navigate = useNavigate()
  const router = { push: navigate as any }
  const { openEnterpriseInviteModal } = useNotificationModals()
  // const { isLatestVersion } = useRequiresUpdate('1.0.15')
  const objectType = activity.object.split(':')[0]
  const postId = activity.data?.parentId ? activity.data.parentId : activity.object.split(':')[1]
  const shouldFetchPost = activity.data?.parentId && (objectType === 'like' || objectType === 'comment')
  const isMatch = activity.verb === NotificationVerb.PARTNER_MATCH_NEW_MESSAGE
  const isNetwork = activity.verb === NotificationVerb.NETWORK_NEW_MESSAGE
  const isSneak = activity.verb === NotificationVerb.SNEAK_NEW_MESSAGE
  const activityVerb = activity.verb
  const matchId = activity.foreignId.split(':')[1]
  const { data: match } = useMatchById(
    [NotificationVerb.PARTNER_MATCH_NEW_MESSAGE].includes(activityVerb) ? Number(matchId) : null,
  )
  const isUnlocked = [NotificationVerb.PARTNER_MATCH_NEW_MESSAGE, NotificationVerb.NEW_MESSAGE].includes(activityVerb)
    ? Boolean(match?.isCreator || match?.recipientUnlockedAt || activity.data?.messageType === 'referral')
    : true

  const { data: post } = useGetPost(postId, {
    enabled: !!shouldFetchPost,
  })

  const getBaseContent = () => {
    try {
      const isAggregated = group.activityCount > 1
      const othersCount = Math.max(0, (group.actorCount || 0) - 1)
      const userName = firstUser ? [firstUser?.firstName, firstUser?.lastName?.[0]].filter(Boolean).join(' ') : 'User'
      const firstActorId = activity.actor.split(':')[1]
      const isSystemActor = firstActorId === 'system' || firstActorId === 'None'

      if (activityVerb === NotificationVerb.WEEKLY_RECAP) {
        return <p className="text-[13px] font-semibold leading-[18px] text-primary">Your weekly recap is ready!</p>
      }
      if (isSystemActor) {
        return (
          <p className="text-[13px] font-semibold leading-[18px] text-primary">
            {activity.data?.text || activity.verb}
          </p>
        )
      }
      if (isMatch && !isUnlocked) {
        return (
          <p className="text-[13px] font-semibold leading-[18px] text-primary">
            Your match of the week sent you a message!
          </p>
        )
      }
      if (isAggregated) {
        if (othersCount > 0) {
          return (
            <p className="text-[13px] font-semibold leading-[18px] text-muted">
              <span className="text-primary">
                {userName} and {othersCount}
              </span>{' '}
              {activity.data?.text || activity.verb}
            </p>
          )
        }
        return (
          <p className="text-[13px] font-semibold leading-[18px] text-muted">
            <span className="text-primary">{userName}</span> {activity.data?.text || activity.verb}
          </p>
        )
      }
      return (
        <p className="text-[13px] font-semibold leading-[18px] text-muted">
          <span className="text-primary">{userName}</span> {activity.data?.text || activity.verb}
        </p>
      )
    } catch (error) {
      console.error('Error extracting get base content:', error, activity)
      return <p className="text-[13px] font-semibold leading-[18px] text-primary">New notification</p>
    }
  }

  const getNotificationOnPress = () => {
    try {
      const linkId = getLinkId(activity)

      if (
        activityVerb === NotificationVerb.PARTNER_MATCH_WEEKLY_RESET ||
        activityVerb === NotificationVerb.PARTNER_MATCH_UNLOCKED
      ) {
        return () => router.push('/referrals')
      }
      if (activityVerb === NotificationVerb.LEAD_CREDIT_REWARD_RECEIVED) {
        return () => router.push('/referrals')
      }
      if (activityVerb === NotificationVerb.LEAD_INQUIRY_RECEIVED) {
        return () => router.push(`/referrals`)
      }
      if (activityVerb === NotificationVerb.LEAD_INQUIRY_REMINDER) {
        return () => router.push(`/referrals`)
      }
      if (activityVerb === NotificationVerb.WEEKLY_RECAP) {
        return () =>
          router.push({
            pathname: '/profile/weekly-recap',
            params: {
              date: activity.time,
            },
          })
      }
      if (activityVerb === NotificationVerb.LICENSE_VERIFICATION) {
        return () => router.push('/profile/licenses')
      }
      if (objectType === 'partner_match') {
        if (!linkId) return null
        return null
      }
      if (objectType === 'like' || objectType === 'comment') {
        if (!linkId) return null
        if (activity.data?.parentType === 'comment') {
          return () => router.push(`/comment/${linkId}`)
        }
        return () => router.push(`/post/${linkId}`)
      }
      if (objectType === 'referral') {
        if (!linkId) return null
        return null
      }
      if (objectType === 'referral_share') {
        if (!linkId) return null

        return null
      }
      if (activityVerb === NotificationVerb.TEAM_INVITATION_APPROVED) {
        if (!linkId) return null
        return () => router.push(`/(drawer)/(tabs)/referrals`)
      }
      if (objectType === 'team_invitation') {
        if (!linkId) return null
        return () => router.push(`/(drawer)/(tabs)/referrals`)
      }
      if (objectType === 'points') {
        return () => router.push('/(drawer)/(tabs)/notifications')
      }
      if (objectType === 'message') {
        if (isMatch && !isUnlocked) {
          return () => router.push('/referrals')
        }

        if (!linkId) return () => router.push(paths.chats.list.getHref())

        if (isMatch)
          return () => router.push({ pathname: `/chat/[channelId]`, params: { channelId: linkId, type: 'network' } })
        if (isNetwork)
          return () => router.push({ pathname: `/chat/[channelId]`, params: { channelId: linkId, type: 'network' } })
        if (isSneak)
          return () =>
            router.push({ pathname: `/chat/[channelId]`, params: { channelId: `sneak-${linkId}`, type: 'sneak' } })

        return () =>
          router.push({
            pathname: `/chat/[channelId]`,
            params: { channelId: `referral-share-${linkId}`, type: 'referral' },
          })
      }
      if (
        activityVerb === NotificationVerb.LYNK_COMPLETED ||
        activityVerb === NotificationVerb.LYNK_NEED_ASSISTANCE ||
        activityVerb === NotificationVerb.LYNK_HOSTILE ||
        activityVerb === NotificationVerb.LYNK_EXTREME_CASE ||
        activityVerb === NotificationVerb.LYNK_OFF_TOPIC ||
        activityVerb === NotificationVerb.LYNK_DISABLED ||
        activityVerb === NotificationVerb.LYNK_FAILED
      ) {
        const lynkData = activity?.data as any
        const chatLink = lynkData?.actionUrl
        if (!chatLink) return null
        return () => router.push({ pathname: chatLink, params: { type: 'consumer' } })
      }
      if (
        activityVerb === NotificationVerb.ENTERPRISE_INVITATION_SENT ||
        activityVerb === NotificationVerb.ENTERPRISE_INVITATION_ACCEPTED ||
        activityVerb === NotificationVerb.ENTERPRISE_INVITATION_DECLINED ||
        activityVerb === NotificationVerb.ENTERPRISE_INVITATION_CANCELED ||
        activityVerb === NotificationVerb.ENTERPRISE_INVITATION_EXPIRED
      ) {
        const enterpriseData = activity?.data as any
        const invitationId = enterpriseData.objectId
        if (!invitationId) return null
        return () => openEnterpriseInviteModal(invitationId as string | number)
      }

      return null
    } catch (error) {
      console.error('Error extracting get notification on press:', error, activity)
      return null
    }
  }

  const getNotificationRightContent = (verb: string) => {
    if (!isValidNotificationVerb(verb)) {
      console.warn(`Unknown notification verb encountered: ${verb}`)
      return null
    }

    if (verb === NotificationVerb.POINTS_RECEIVED) {
      const pointData = activity?.data as any
      return <PointRightContent points={pointData.pointsAmount} />
    } else if (
      verb === NotificationVerb.NEW_LIKE_ON_POST ||
      verb === NotificationVerb.NEW_LIKE_ON_COMMENT ||
      verb === NotificationVerb.NEW_COMMENT_ON_POST ||
      verb === NotificationVerb.NEW_COMMENT_ON_COMMENT
    ) {
      return <FeedRightContent image={getNotificationImage() || ''} />
    }

    return null
  }

  const getNotificationImage = () => {
    const isFeed = objectType === 'like' || objectType === 'comment'
    const hasImages = post?.images && post.images.length > 0

    if (isFeed && hasImages) {
      return post.images[0]
    }
    return null
  }

  const getIcon = (verb: string) => {
    if (!isValidNotificationVerb(verb)) {
      console.warn(`Unknown notification verb encountered: ${verb}`)
      return <MemoizedSystemIcon source={SystemIconImg} className="bg-primary" />
    }
    return NotificationIconMap[verb] ? (
      NotificationIconMap[verb](activity)
    ) : (
      <MemoizedSystemIcon source={SystemIconImg} className="bg-primary" />
    )
  }

  const getNotificationTitle = (verb: string) => {
    if (!isValidNotificationVerb(verb)) {
      console.warn(`Unknown notification verb encountered: ${verb}`)
      return null
    }

    if (
      verb === NotificationVerb.TEAM_INVITATION_APPROVED ||
      verb === NotificationVerb.TEAM_INVITATION_DECLINED_BY_INVITEE ||
      verb === NotificationVerb.TEAM_INVITATION_DECLINED_BY_CONSUMER ||
      verb === NotificationVerb.TEAM_INVITATION_EXPIRED ||
      verb === NotificationVerb.TEAM_INVITATION_SENT ||
      verb === NotificationVerb.TEAM_INVITATION_INTERESTED ||
      verb === NotificationVerb.TEAM_INVITATION_AUTO_REJECTED ||
      verb === NotificationVerb.POINTS_RECEIVED ||
      verb === NotificationVerb.TWILIO_BRAND_FAILED ||
      verb === NotificationVerb.TWILIO_CAMPAIGN_FAILED ||
      verb === NotificationVerb.TWILIO_NUMBER_SELECTION ||
      verb === NotificationVerb.LYNK_COMPLETED ||
      verb === NotificationVerb.LYNK_NEED_ASSISTANCE ||
      verb === NotificationVerb.LYNK_HOSTILE ||
      verb === NotificationVerb.LYNK_EXTREME_CASE ||
      verb === NotificationVerb.LYNK_OFF_TOPIC ||
      verb === NotificationVerb.LYNK_DISABLED ||
      verb === NotificationVerb.LYNK_FAILED ||
      verb === NotificationVerb.ENTERPRISE_INVITATION_SENT ||
      verb === NotificationVerb.ENTERPRISE_INVITATION_ACCEPTED ||
      verb === NotificationVerb.ENTERPRISE_INVITATION_DECLINED ||
      verb === NotificationVerb.ENTERPRISE_INVITATION_CANCELED ||
      verb === NotificationVerb.ENTERPRISE_INVITATION_EXPIRED
    ) {
      return NotificationTitleMap[verb] ? NotificationTitleMap[verb](activity) : null
    }

    return getBaseContent()
  }

  const getNotificationSubtitle = (verb: string) => {
    if (!isValidNotificationVerb(verb)) {
      console.warn(`Unknown notification verb encountered: ${verb}`)
      return null
    }

    if (
      verb === NotificationVerb.TEAM_INVITATION_APPROVED ||
      verb === NotificationVerb.TEAM_INVITATION_DECLINED_BY_INVITEE ||
      verb === NotificationVerb.TEAM_INVITATION_DECLINED_BY_CONSUMER ||
      verb === NotificationVerb.TEAM_INVITATION_EXPIRED ||
      verb === NotificationVerb.TEAM_INVITATION_SENT ||
      verb === NotificationVerb.TEAM_INVITATION_INTERESTED ||
      verb === NotificationVerb.TEAM_INVITATION_AUTO_REJECTED ||
      verb === NotificationVerb.POINTS_RECEIVED ||
      verb === NotificationVerb.TWILIO_BRAND_FAILED ||
      verb === NotificationVerb.TWILIO_CAMPAIGN_FAILED ||
      verb === NotificationVerb.TWILIO_NUMBER_SELECTION ||
      verb === NotificationVerb.LYNK_COMPLETED ||
      verb === NotificationVerb.LYNK_NEED_ASSISTANCE ||
      verb === NotificationVerb.LYNK_HOSTILE ||
      verb === NotificationVerb.LYNK_EXTREME_CASE ||
      verb === NotificationVerb.LYNK_OFF_TOPIC ||
      verb === NotificationVerb.LYNK_DISABLED ||
      verb === NotificationVerb.LYNK_FAILED ||
      verb === NotificationVerb.ENTERPRISE_INVITATION_SENT ||
      verb === NotificationVerb.ENTERPRISE_INVITATION_ACCEPTED ||
      verb === NotificationVerb.ENTERPRISE_INVITATION_DECLINED ||
      verb === NotificationVerb.ENTERPRISE_INVITATION_CANCELED ||
      verb === NotificationVerb.ENTERPRISE_INVITATION_EXPIRED
    ) {
      return NotificationSubtitleMap[verb] ? NotificationSubtitleMap[verb](activity) : null
    }

    return null
  }

  return {
    text: getBaseContent(),
    onPress: getNotificationOnPress(),
    image: getNotificationImage(),
    icon: getIcon(activityVerb),
    title: getNotificationTitle(activityVerb),
    subtitle: getNotificationSubtitle(activityVerb),
    rightContent: getNotificationRightContent(activityVerb),
  }
}

export default useUserNotificationContent
